import { useState, useEffect, useCallback, useRef } from 'react';

interface PaginationResponse<T> {
  success: boolean;
  total: number;
  per_page: number;
  page: number;
  data: {
    applicants: T[];
  };
}

interface UseInfiniteScrollOptions<T> {
  fetchFunction: (page: number, per_page: number) => Promise<PaginationResponse<T>>;
  perPage?: number;
  enabled?: boolean;
}

interface UseInfiniteScrollReturn<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reset: () => void;
  total: number;
}

export function useInfiniteScroll<T>({
  fetchFunction,
  perPage = 5,
  enabled = true
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const loadingRef = useRef(false);

  const loadMore = useCallback(async () => {
    if (!enabled || loadingRef.current || !hasMore) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      const response = await fetchFunction(currentPage, perPage);
      
      if (response.success) {
        const newApplicants = response.data.applicants || [];
        setData(prev => [...prev, ...newApplicants]);
        setTotal(response.total);
        setCurrentPage(prev => prev + 1);
        
        // Check if we've loaded all data
        const totalLoaded = data.length + newApplicants.length;
        setHasMore(totalLoaded < response.total);
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error('Error loading more data:', err);
      setError('Failed to load more candidates');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [enabled, hasMore, currentPage, perPage, fetchFunction, data.length]);

  const reset = useCallback(() => {
    setData([]);
    setLoading(false);
    setError(null);
    setHasMore(true);
    setCurrentPage(1);
    setTotal(0);
    loadingRef.current = false;
  }, []);

  // Reset when enabled changes and load initial data
  useEffect(() => {
    if (enabled) {
      // Reset state first
      setData([]);
      setLoading(false);
      setError(null);
      setHasMore(true);
      setCurrentPage(1);
      setTotal(0);
      loadingRef.current = false;
      
      // Load initial data
      const loadInitial = async () => {
        try {
          loadingRef.current = true;
          setLoading(true);
          setError(null);

          const response = await fetchFunction(1, perPage);
          
          if (response.success) {
            const newApplicants = response.data.applicants || [];
            setData(newApplicants);
            setTotal(response.total);
            setCurrentPage(2); // Next page will be 2
            
            // Check if we have more data
            setHasMore(newApplicants.length < response.total);
          } else {
            setError('Failed to load data');
          }
        } catch (err) {
          console.error('Error loading initial data:', err);
          setError('Failed to load candidates');
        } finally {
          setLoading(false);
          loadingRef.current = false;
        }
      };
      
      loadInitial();
    }
  }, [enabled, fetchFunction, perPage]);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
    total
  };
} 